import _ from 'lodash';
import buildUrl from 'build-url';
import queryString from 'query-string';
import cryptoRandomString from 'crypto-random-string';

import {action, observable, reaction, toJS} from 'mobx';

import {resetSessionBootstrap, setLogoutFunction, setSessionProvider} from '../shared/fetch-json.util';
import AnalyticsService from '../shared/services/analytics.service';
import GuestUserService from '../shared/services/guest-user.service';

const SESSION_STORAGE_KEY = 'pc-user-session';
const STATE_STORAGE_KEY = 'pc-auth-state';
const CODE_VERIFIER_STORAGE_KEY = 'pc-auth-code-verifier';
// The return route is also stashed here, not just inside the base64 `state` round-tripped through
// Okta. sessionStorage survives the redirect (same origin, same tab) and cannot be mangled by URL
// encoding, so it is the authoritative copy - losing the route params here means landing back on
// the app without locale-cd/country-cd/dealer-id/cart-id, which silently starves every
// parameter-gated call (footer info, profile dealers, carts) until the page is reloaded.
const RETURN_ROUTE_STORAGE_KEY = 'pc-auth-return-route';
const CRYPTO_LENGTH = 32;
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
// The proxy/mock session contract emits ANONYMOUS | GUEST_USER | REGISTERED_USER, while the
// Phase 1 spec documented AUTHENTICATED. Accept every spelling so a registered session is never
// silently downgraded to the guest branch (which renders the Sign In screen again).
const REGISTERED_SESSION_TYPES = ['AUTHENTICATED', 'REGISTERED', 'REGISTERED_USER'];

class PCUserSession {
    @observable _loggingIn = false;
    @observable _loginFailed = false;
    @observable _guestIsRegistered = false;
    @observable _currentUser = null;

    constructor(launchParams, baseStore, routerStore) {
        this.baseStore = baseStore;
        this.routerStore = routerStore;
        setLogoutFunction(this.logout);
        setSessionProvider(() => {
            return Boolean(this._currentUser);
        });

        const parsedLaunchParams = this.parseLaunchParams(launchParams);
        const expectedState = window.sessionStorage.getItem(STATE_STORAGE_KEY);
        const codeVerifier = window.sessionStorage.getItem(CODE_VERIFIER_STORAGE_KEY);

        if (!expectedState || !codeVerifier) {
            this.restoreStoredSession();
            this.refreshCurrentSession();
            return;
        }

        this.handleAuthCodeCallback(parsedLaunchParams);

        // when the user is updated save to session storage
        reaction(
            () => this._currentUser,
            (currentUser) => {
                if (currentUser) {
                    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
                        currentUser: currentUser
                    }));
                } else {
                    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
                }
            },
            {
                fireImmediately: true
            }
        );
    }

    parseLaunchParams(launchParams) {
        if (!launchParams) {
            return {};
        }

        return queryString.parse(launchParams.replace(/^[#?]/, ''));
    }

    @action
    restoreStoredSession() {
        const storedSession = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

        if (!storedSession) {
            return;
        }

        try {
            const storedSessionObj = JSON.parse(storedSession);

            this._currentUser = storedSessionObj.currentUser;
        } catch (err) {
            window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
    }

    getRedirectUri() {
        return buildUrl(
            WEBPACK_ROOT_URL,
            {
                path: '/authenticate'
            }
        );
    }

    createCodeChallenge(codeVerifier) {
        if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) {
            return Promise.reject(new Error('PKCE not supported'));
        }

        return window.crypto.subtle.digest(
            'SHA-256',
            new window.TextEncoder().encode(codeVerifier)
        ).then((hashBuffer) => {
            const byteArray = new Uint8Array(hashBuffer);
            let base64 = '';

            _.forEach(byteArray, (byte) => {
                base64 += String.fromCharCode(byte);
            });

            return btoa(base64)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(new RegExp('=+$'), '');
        });
    }

    /**
     * Resolves the greeting name ("Hi, {name}") from a session document.
     *
     * Before KART-1674 this value came from the id_token claim `name`, decoded in the browser.
     * Now it has to come from `GET /v2/auth/session/me`, and the proxy does not consistently use
     * a single field name for it - so probe every shape it is known to emit before giving up.
     * Returning `undefined` here is what produced the "Hi," with no name.
     * @param {Object} session The session document returned by the backend.
     * @returns {String|undefined} The name to greet the user with.
     */
    resolveDisplayName(session) {
        const direct = _.find(
            [
                session.displayName,
                session.name,
                session.fullName,
                session.userName,
                session.preferredUsername,
                session.preferred_username
            ],
            (value) => _.isString(value) && value.trim() !== ''
        );

        if (direct) {
            return direct.trim();
        }

        const first = session.firstName || session.givenName || session.given_name;
        const last = session.lastName || session.familyName || session.family_name;
        const composed = _.compact([first, last]).join(' ').trim();

        if (composed) {
            return composed;
        }

        // Last resort: an email is still friendlier than an empty greeting.
        return session.email || session.emailId || session.emailID || undefined;
    }

    @action
    applySessionToUser(session, fallbackGuestEmail) {
        const sessionType = _.toUpper(_.trim(_.get(session, 'sessionType')));

        if (!session || !sessionType || sessionType === 'ANONYMOUS') {
            this._currentUser = null;
            return true;
        }

        if (_.includes(REGISTERED_SESSION_TYPES, sessionType)) {
            if (!session.userId) {
                // The backend should never send this shape (SessionLifecycleService now rejects
                // an AUTHENTICATED upgrade with no "userID" claim before persisting the session),
                // but if it ever does, `loginVisible` would silently treat the missing id as
                // logged-out and bounce back to the Sign In screen with no visible error - even
                // though the token exchange and /session/me calls both returned 200. Surface it
                // as a critical error instead of a phantom login loop.
                this._currentUser = null;
                return false;
            }

            const resolvedName = this.resolveDisplayName(session);

            this._currentUser = {
                id: session.userId,
                userName: session.userId,
                // Fall back to the previously stored name so a /me refresh that omits the field
                // does not blank out a greeting that was already correct.
                name: resolvedName || _.get(this._currentUser, 'name') || session.userId,
                countryCode: session.countryCode || session.country || _.get(this.baseStore, 'countryCode')
            };
            return true;
        }

        this._currentUser = {
            idEmail: fallbackGuestEmail ||
                session.email ||
                session.displayName ||
                _.get(this._currentUser, 'idEmail')
        };
        return true;
    }

    @action
    refreshCurrentSession() {
        this._loggingIn = true;

        return GuestUserService.fetchSession()
            .then(action((session) => {
                this.applySessionToUser(session);
            }))
            .catch(action(() => {
                this._currentUser = null;
            }))
            .then(action(() => {
                this._loggingIn = false;
            }));
    }

    @action
    handleAuthCodeCallback(parsedLaunchParams) {
        this._loggingIn = true;
        const authCode = _.get(parsedLaunchParams, 'code');
        const encodedState = _.get(parsedLaunchParams, 'state');

        if (!authCode || !encodedState) {
            window.sessionStorage.removeItem(STATE_STORAGE_KEY);
            window.sessionStorage.removeItem(CODE_VERIFIER_STORAGE_KEY);
            this.baseStore.showCriticalError();
            this._loggingIn = false;
            return Promise.resolve();
        }

        let decodedState;

        try {
            decodedState = JSON.parse(atob(encodedState));
        } catch (err) {
            window.sessionStorage.removeItem(STATE_STORAGE_KEY);
            window.sessionStorage.removeItem(CODE_VERIFIER_STORAGE_KEY);
            this.baseStore.showCriticalError();
            this._loggingIn = false;
            return Promise.resolve();
        }

        const expectedState = window.sessionStorage.getItem(STATE_STORAGE_KEY);
        const codeVerifier = window.sessionStorage.getItem(CODE_VERIFIER_STORAGE_KEY);

        if (!expectedState || decodedState.nonce !== expectedState || !codeVerifier) {
            this.baseStore.showCriticalError();
            this._loggingIn = false;
            return Promise.resolve();
        }

        window.sessionStorage.removeItem(STATE_STORAGE_KEY);
        window.sessionStorage.removeItem(CODE_VERIFIER_STORAGE_KEY);

        const returnRoute = this.readReturnRoute(decodedState);

        return GuestUserService.upgradeSsoSession(
            authCode,
            this.getRedirectUri(),
            codeVerifier
        ).then(() => {
            return GuestUserService.fetchSession();
        }).then(
            action((session) => {
                if (!this.applySessionToUser(session)) {
                    // Okta/token-exchange calls all returned 200, but the resulting session has
                    // no usable identity - do not navigate to the protected route with no user;
                    // that would render the Sign In screen again with no visible failure.
                    this.baseStore.showCriticalError();
                    return;
                }
                AnalyticsService.writeUserSignedInEvent('registered');
                this.routerStore.router.navigate(
                    returnRoute.routeName,
                    returnRoute.routeParams,
                    {
                        replace: true
                    }
                );
            })
        ).catch(
            action(() => {
                this.baseStore.showCriticalError();
            })
        ).then(
            action(() => {
                this._loggingIn = false;
            }),
            action(() => {
                this._loggingIn = false;
            })
        );
    }

    /**
     * Resolves the route to return to after the Okta round trip. Prefers the sessionStorage copy
     * written by loginAsRegistered(): the `state` parameter travels through Okta and back as a
     * base64 blob in the query string, so it is the copy at risk of being truncated or re-encoded.
     * Losing the params leaves the app on the target route without locale-cd/country-cd/
     * dealer-id/cart-id, and every parameter-gated reaction (footer info, profile dealers, carts)
     * then sits idle until the user reloads the page.
     * @param {Object} decodedState The state payload returned by the identity provider.
     * @returns {Object} `{routeName, routeParams}` for the post-login navigation.
     */
    readReturnRoute(decodedState) {
        const fallback = {
            routeName: _.get(decodedState, 'routeName'),
            routeParams: _.get(decodedState, 'routeParams') || {}
        };
        let stored;

        try {
            stored = JSON.parse(window.sessionStorage.getItem(RETURN_ROUTE_STORAGE_KEY));
        } catch (err) {
            stored = null;
        }
        window.sessionStorage.removeItem(RETURN_ROUTE_STORAGE_KEY);

        if (!stored || !stored.routeName) {
            return fallback;
        }

        return {
            routeName: stored.routeName,
            // keep whichever copy actually carries the params
            routeParams: _.isEmpty(stored.routeParams) ? fallback.routeParams : stored.routeParams
        };
    }

    @action.bound
    loginAsRegistered() {
        this._loggingIn = true;
        this._loginFailed = false;

        const state = cryptoRandomString(CRYPTO_LENGTH);
        const codeVerifier = cryptoRandomString(CRYPTO_LENGTH * 2);
        // toJS: route.params is a MobX observable - serialize a plain snapshot so the params
        // survive both sessionStorage and the base64 `state` round trip through Okta.
        const returnRoute = {
            routeName: toJS(this.routerStore.route.name),
            routeParams: toJS(this.routerStore.route.params) || {}
        };

        window.sessionStorage.setItem(STATE_STORAGE_KEY, state);
        window.sessionStorage.setItem(CODE_VERIFIER_STORAGE_KEY, codeVerifier);
        window.sessionStorage.setItem(RETURN_ROUTE_STORAGE_KEY, JSON.stringify(returnRoute));

        return this.createCodeChallenge(codeVerifier).then(
            action((codeChallenge) => {
                window.location.replace(buildUrl(AUTH_LOGIN_URL, {
                    queryParams: {
                        'client_id': AUTH_CLIENT_ID,
                        'response_mode': 'query',
                        'response_type': 'code',
                        'scope': 'openid customer_profile profile',
                        'redirect_uri': this.getRedirectUri(),
                        'state': btoa(JSON.stringify({
                            'nonce': state,
                            'routeName': returnRoute.routeName,
                            'routeParams': returnRoute.routeParams
                        })),
                        'code_challenge_method': 'S256',
                        'code_challenge': codeChallenge
                    }
                }));
            })
        ).catch(
            action(() => {
                this._loggingIn = false;
                this.baseStore.showCriticalError();
            })
        );
    }

    @action.bound
    loginAsGuest(email) {
        this._loggingIn = true;
        this._loginFailed = false;
        this._guestIsRegistered = false;

        const cartId = _.get(this.routerStore, 'route.params.cart-id');

        GuestUserService.upgradeGuestSession(email, cartId)
            .then(() => {
                return GuestUserService.fetchSession();
            })
            .then(action((session) => {
                this._loggingIn = false;
                this.applySessionToUser(session, email);
                AnalyticsService.writeUserSignedInEvent('guest');
            }))
            .catch(action((error) => {
                this._loggingIn = false;
                if (error && (error.status === HTTP_FORBIDDEN || error.status === HTTP_CONFLICT)) {
                    this._guestIsRegistered = true;
                    return;
                }
                this._loginFailed = true;
            }));
    }

    @action.bound
    logout() {
        const userId = toJS(_.get(this._currentUser, 'id'));

        // clear variables
        this._currentUser = null;
        this._loginFailed = false;
        this._guestIsRegistered = false;
        window.sessionStorage.removeItem(STATE_STORAGE_KEY);
        window.sessionStorage.removeItem(CODE_VERIFIER_STORAGE_KEY);
        // The bootstrap promise in fetch-json.util caches the fact that a session was already
        // established. Today logout always ends in a full page navigation which wipes it, but
        // clearing it explicitly keeps ensureSession honest if that ever stops being true -
        // otherwise the next call would skip re-bootstrapping and run without a session cookie.
        resetSessionBootstrap();

        this._loggingIn = true;
        return GuestUserService.logOff().then(
            action(() => {
                if (userId) {
                    window.location.replace(buildUrl(AUTH_LOGOUT_URL, {
                        queryParams: {
                            // this must be static so is a direct link to the root instead of makePartsCatalogUrl
                            'post_logout_redirect_uri': PARTS_CATALOG_URL
                        }
                    }));

                    // checkout from myjohndeere
                    window.location.replace(buildUrl(MJD_LOGOUT_URL));
                } else {
                    // logged in as a guest, redirect to the parts catalog
                    window.location.href = PARTS_CATALOG_URL;
                }
            }),
            action(() => {
                if (userId) {
                    window.location.replace(buildUrl(AUTH_LOGOUT_URL, {
                        queryParams: {
                            // this must be static so is a direct link to the root instead of makePartsCatalogUrl
                            'post_logout_redirect_uri': PARTS_CATALOG_URL
                        }
                    }));

                    // checkout from myjohndeere
                    window.location.replace(buildUrl(MJD_LOGOUT_URL));
                } else {
                    // logged in as a guest, redirect to the parts catalog
                    window.location.href = PARTS_CATALOG_URL;
                }
            })
        );
    }

    get currentUser() {
        return this._currentUser;
    }
    get loggingIn() {
        return this._loggingIn;
    }
    get loginFailed() {
        return this._loginFailed;
    }
    @action.bound
    resetGuest() {
        this._guestIsRegistered = false;
    }
    get guestIsRegistered() {
        return this._guestIsRegistered;
    }
}

export default PCUserSession;
